import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Button, Chip, CircularProgress, TextField, Typography } from '@mui/material';
import MicIcon from '@mui/icons-material/Mic';
import StopIcon from '@mui/icons-material/Stop';

import {
  OnboardingAudioCaptureError,
  onboardingAudioCaptureService,
} from '../../services/onboardingAudioCaptureService';
import { draftPersonaExample } from '../../services/playbookPersonaApi';

const MAX_BRIEF_CHARS = 8000;

const ERROR_COPY: Record<string, string> = {
  'permission-denied':
    'Your browser blocked microphone access. Allow it for this site, or just type the description instead.',
  'no-device': 'No microphone was found. You can type the description instead.',
  'connection-failed':
    'We could not reach the speech service. You can type the description instead.',
  unknown: 'Dictation is unavailable right now. You can type the description instead.',
};

// Keyed by the API error code; anything else falls back to GENERIC.
const EXAMPLE_ERROR_COPY: Record<string, string> = {
  COMPANY_CONTEXT_INCOMPLETE:
    'Finish your company details first, so the example can be written for what you sell.',
  GENERIC: 'We could not write an example right now. Try again, or describe the customer in your own words.',
};

interface PersonaDictationProps {
  brief: string;
  onBriefChange: (brief: string) => void;
  /** Called after an example replaces the description. */
  onExampleApplied?: () => void;
  disabled?: boolean;
}

/**
 * Captures the admin's description of the AI customer, spoken or typed.
 *
 * Dictation streams to the same Deepgram proxy the onboarding wizard already
 * uses. Interim results are shown but never committed — only finals are
 * appended — so the textarea does not thrash while someone is mid-sentence.
 *
 * Dictation is always optional: every failure path degrades to typing rather
 * than blocking, because an admin who cannot use a microphone still has to be
 * able to build a track.
 */
export const PersonaDictation: React.FC<PersonaDictationProps> = ({
  brief,
  onBriefChange,
  onExampleApplied,
  disabled = false,
}) => {
  const [isRecording, setIsRecording] = useState(false);
  const [interim, setInterim] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isLoadingExample, setIsLoadingExample] = useState(false);
  const briefRef = useRef(brief);

  useEffect(() => {
    briefRef.current = brief;
  }, [brief]);

  // Stop the microphone if the admin navigates away mid-dictation.
  useEffect(() => () => { onboardingAudioCaptureService.stopCapture(); }, []);

  const handleStop = useCallback(() => {
    onboardingAudioCaptureService.stopCapture();
    setIsRecording(false);
    setInterim('');
  }, []);

  const handleStart = useCallback(async () => {
    setError(null);
    try {
      await onboardingAudioCaptureService.startCapture((result) => {
        if (!result.transcript) return;
        if (!result.isFinal) {
          setInterim(result.transcript);
          return;
        }
        setInterim('');
        const joined = `${briefRef.current} ${result.transcript}`.trim();
        onBriefChange(joined.slice(0, MAX_BRIEF_CHARS));
      });
      setIsRecording(true);
    } catch (captureError) {
      const reason =
        captureError instanceof OnboardingAudioCaptureError ? captureError.reason : 'unknown';
      setError(ERROR_COPY[reason] || ERROR_COPY.unknown);
      setIsRecording(false);
    }
  }, [onBriefChange]);

  // An example REPLACES whatever is in the textarea, so an admin can swap an
  // old description for a fresh one; the textarea is locked while it loads.
  const handleUseExample = useCallback(async () => {
    setError(null);
    setIsLoadingExample(true);
    try {
      const example = await draftPersonaExample();
      if (!example) throw new Error('empty example');
      onBriefChange(example.slice(0, MAX_BRIEF_CHARS));
      onExampleApplied?.();
    } catch (exampleError: any) {
      console.error('Error drafting persona example:', exampleError);
      setError(EXAMPLE_ERROR_COPY[exampleError?.error] || EXAMPLE_ERROR_COPY.GENERIC);
    } finally {
      setIsLoadingExample(false);
    }
  }, [onBriefChange, onExampleApplied]);

  const remaining = MAX_BRIEF_CHARS - brief.length;
  const isInputLocked = disabled || isLoadingExample;

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Describe the customer you want the salesperson to practise against: who they are,
        what they are trying to buy, and what they should push back on. Talk the way you
        would brief a colleague; it does not need to be tidy.
      </Typography>

      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1.5 }}>
        <Button
          variant={isRecording ? 'contained' : 'outlined'}
          color={isRecording ? 'error' : 'primary'}
          startIcon={isRecording ? <StopIcon /> : <MicIcon />}
          onClick={isRecording ? handleStop : handleStart}
          disabled={isInputLocked}
        >
          {isRecording ? 'Stop dictating' : 'Dictate'}
        </Button>
        {isRecording && (
          <Chip size="small" color="error" variant="outlined" label="Listening…" />
        )}
        {!isRecording && (
          <Button
            size="small"
            onClick={handleUseExample}
            disabled={isInputLocked}
            startIcon={isLoadingExample ? <CircularProgress size={14} color="inherit" /> : undefined}
          >
            {isLoadingExample ? 'Writing an example…' : 'Use an example'}
          </Button>
        )}
      </Box>

      {error && (
        <Typography variant="caption" color="error" sx={{ display: 'block', mb: 1 }}>
          {error}
        </Typography>
      )}

      <TextField
        fullWidth
        multiline
        minRows={6}
        maxRows={16}
        value={interim ? `${brief} ${interim}`.trim() : brief}
        onChange={(event) => onBriefChange(event.target.value.slice(0, MAX_BRIEF_CHARS))}
        placeholder="Speak or type your description of the AI customer…"
        disabled={isInputLocked}
        helperText={
          interim
            ? 'Still listening. What you see will settle when you pause.'
            : `${remaining.toLocaleString()} characters left. You can edit anything you dictated.`
        }
      />
    </Box>
  );
};

export default PersonaDictation;
